/**
 * judgeme-verified-badge.js
 *
 * Injects Judge.me verified buyer badges into imported reviews
 * (eBay, Trustoo, etc.) that are missing the badge markup.
 *
 * Fixes:
 * - Uses :has(.jm-reviewer-info__name) to target ONLY the name cluster,
 *   not the date/location cluster.
 * - Marks injected badges with a custom class so we never double-inject
 *   or conflict with badges Vue renders natively.
 */

(function () {
  'use strict';

  const INJECTED_CLASS    = 'jm-verified-buyer-badge--injected';
  const PROCESSED_ATTR    = 'data-jm-badge-done';

  const BADGE_HTML = `
    <div class="jm-verified-buyer-badge ${INJECTED_CLASS}" data-v-2cfdbb03="">
      <svg data-v-2cfdbb03="" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.8335 5.79102C10.8799 5.79107 10.9302 5.80376 11.0005 5.87402C11.0669 5.94051 11.0835 5.99334 11.0835 6.05176C11.0834 6.11009 11.0669 6.16305 11.0005 6.22949L7.10498 10.125C7.03455 10.1954 6.98353 10.208 6.93701 10.208C6.89059 10.208 6.84029 10.1953 6.77002 10.125L4.99951 8.35449C4.93307 8.28805 4.91656 8.23509 4.9165 8.17676C4.9165 8.1183 4.93295 8.06559 4.99951 7.99902C5.06994 7.92859 5.11999 7.91602 5.1665 7.91602C5.21302 7.91602 5.26307 7.92859 5.3335 7.99902L5.33643 8.00195L6.58643 9.23047L6.93896 9.57715L7.28955 9.22852L10.6646 5.875L10.6655 5.87402C10.736 5.8036 10.787 5.79102 10.8335 5.79102Z" stroke="currentColor"></path>
        <path d="M8 0.5C9.04675 0.5 10.0169 0.695953 10.916 1.08398C11.8245 1.47612 12.6191 2.01365 13.3027 2.69727C13.9863 3.38088 14.5239 4.17555 14.916 5.08398C15.304 5.98307 15.5 6.95325 15.5 8C15.5 9.03209 15.304 9.99957 14.915 10.9072C14.5228 11.8225 13.9857 12.6198 13.3027 13.3027C12.6191 13.9863 11.8245 14.5239 10.916 14.916C10.0169 15.304 9.04675 15.5 8 15.5C6.96791 15.5 6.00043 15.304 5.09277 14.915C4.17755 14.5228 3.38025 13.9857 2.69727 13.3027C2.01428 12.6198 1.47722 11.8225 1.08496 10.9072C0.695967 9.99957 0.5 9.03209 0.5 8C0.5 6.95325 0.695953 5.98307 1.08398 5.08398C1.47612 4.17555 2.01365 3.38088 2.69727 2.69727C3.38025 2.01428 4.17755 1.47722 5.09277 1.08496C6.00043 0.695967 6.96791 0.5 8 0.5Z" stroke="currentColor"></path>
      </svg>
    </div>
  `;

  function processReviews() {
    // :not([data-jm-badge-done]) means each nameEl is processed exactly once,
    // regardless of how many times processReviews is called.
    const nameEls = document.querySelectorAll(
      `.jm-reviewer-info__name:not([${PROCESSED_ATTR}])`
    );

    nameEls.forEach(function (nameEl) {
      // Stamp the name element immediately — before any early returns —
      // so re-runs of processReviews never touch this element again.
      nameEl.setAttribute(PROCESSED_ATTR, 'true');

      // Walk up to the direct .jm-cluster parent (the name row).
      const cluster = nameEl.closest('.jm-cluster');
      if (!cluster) return;

      // Skip if a native Judge.me verified badge is already present.
      const hasNativeBadge = !!cluster.querySelector(
        `.jm-verified-buyer-badge:not(.${INJECTED_CLASS})`
      );
      // Skip if a "Verified by Shop" badge is already present.
      const hasShopBadge = !!cluster.querySelector('.jm-verified-by-shop-badge');

      if (hasNativeBadge || hasShopBadge) return;

      cluster.insertAdjacentHTML('beforeend', BADGE_HTML);
    });
  }

  function startObserver() {
    const observer = new MutationObserver(function (mutations) {
      // Only re-run if Judge.me added new unprocessed name elements.
      // This prevents our own badge injections from re-triggering the observer.
      const hasNewNames = mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).some(function (node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          // Ignore our own injected badge nodes
          if (node.classList.contains(INJECTED_CLASS)) return false;
          return (
            node.classList.contains('jm-reviewer-info__name') ||
            node.querySelector(`.jm-reviewer-info__name:not([${PROCESSED_ATTR}])`)
          );
        });
      });

      if (hasNewNames) {
        setTimeout(processReviews, 200);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    processReviews();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();